import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Returns the appropriate CSS colour class for a language toggle label.
const getLangClass = (isActive, onDark) => {
  if (isActive) return 'text-winery-gold';
  return onDark ? 'text-white/35' : 'text-obsidian/35';
};

// Extracted as a standalone component so Navbar's own complexity stays low.
const LangToggle = ({ onDark }) => {
  const { lang, toggleLang } = useLanguage();
  return (
    <button
      data-testid="lang-toggle"
      onClick={toggleLang}
      aria-label="Toggle language"
      className="font-body text-xs tracking-[0.15em] flex items-center gap-1 transition-colors duration-300"
    >
      <span className={`transition-colors duration-300 ${getLangClass(lang === 'bg', onDark)}`}>BG</span>
      <span className={onDark ? 'text-white/20' : 'text-obsidian/20'}>/</span>
      <span className={`transition-colors duration-300 ${getLangClass(lang === 'en', onDark)}`}>EN</span>
    </button>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // `onScroll` is defined inside the effect; `setScrolled` is a stable setter — register once on mount
  }, []);

  const navLinkClass = scrolled
    ? 'text-obsidian/70 hover:text-obsidian'
    : 'text-white/80 hover:text-white';

  const ctaClass = scrolled
    ? 'border-obsidian text-obsidian hover:bg-obsidian hover:text-cream'
    : 'border-white/70 text-white hover:bg-white/10';

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-cream/95 backdrop-blur-sm border-b border-winery-border'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
          <a
            href="#home"
            data-testid="navbar-brand"
            className={`font-heading font-light tracking-[0.2em] text-sm md:text-base transition-colors duration-500 ${
              scrolled ? 'text-obsidian' : 'text-white'
            }`}
          >
            GERANIUM BOHEMICUM
          </a>

          <div className="hidden md:flex items-center gap-10">
            {t.nav.links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.href.replace('#', '')}`}
                className={`nav-link font-body text-xs tracking-widest uppercase transition-colors duration-500 ${navLinkClass}`}
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-5">
            <LangToggle onDark={!scrolled} />
            <a
              href="#contact"
              data-testid="navbar-cta"
              className={`hidden md:inline-block font-body text-xs tracking-widest uppercase px-6 py-3 border transition-all duration-300 ${ctaClass}`}
            >
              {t.nav.cta}
            </a>
            <button
              data-testid="navbar-hamburger"
              onClick={() => setMenuOpen(true)}
              className={`md:hidden transition-colors duration-500 ${
                scrolled ? 'text-obsidian' : 'text-white'
              }`}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </nav>

      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-50 bg-cream mobile-menu ${menuOpen ? 'open' : ''}`}
      >
        <div className="flex flex-col h-full px-8 py-8">
          <div className="flex justify-between items-center mb-16">
            <span className="font-heading font-light tracking-[0.2em] text-sm text-obsidian">
              GERANIUM BOHEMICUM
            </span>
            <button
              data-testid="mobile-menu-close"
              onClick={() => setMenuOpen(false)}
              className="text-obsidian"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>

          <nav className="flex flex-col gap-8">
            {t.nav.links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`mobile-nav-${l.href.replace('#', '')}`}
                onClick={() => setMenuOpen(false)}
                className="font-heading text-4xl font-light text-obsidian hover:text-winery-gold transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="mobile-nav-contact"
              onClick={() => setMenuOpen(false)}
              className="font-heading text-4xl font-light text-obsidian hover:text-winery-gold transition-colors duration-300"
            >
              {t.nav.cta}
            </a>
          </nav>

          <div className="mt-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="line-gold flex-1 mr-6" />
              <LangToggle onDark={false} />
            </div>
            <p className="font-body text-xs tracking-widest uppercase text-winery-text mt-4">
              {t.nav.mobileFooter}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
